import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Save, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { shiftService, CreateShiftData } from '../../../services/shiftService'
import { extractApiErrors } from '../../../utils/errorHandler'

export function ShiftCreatePage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<CreateShiftData>({
    code: '',
    name: '',
    description: null,
    type: '5R',
    isActive: true,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const newShift = await shiftService.create(formData)
      toast.success('Turno creado correctamente')
      navigate(`/configuration/shifts/${newShift.id}`)
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0 ? details.map(d => d.message).join('. ') : undefined,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="animate-slide-in">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Link
            to="/configuration/shifts"
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Clock className="h-6 w-6" />
              Nuevo Turno
            </h1>
            <p className="page-subtitle">
              Crea un nuevo turno de trabajo
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="card">
          <div className="space-y-6">
            <div>
              <label className="label">Código del turno *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="input w-full"
                placeholder="Ej: A, B, C, R3"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Código único para identificar el turno (ej: A, B, C, D, E, R3)
              </p>
            </div>

            <div>
              <label className="label">Nombre del turno *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                placeholder="Ej: Turno A, Turno 3R (Plisado)"
                required
              />
            </div>

            <div>
              <label className="label">Descripción</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value || null })}
                className="input w-full"
                placeholder="Descripción opcional del turno"
                rows={3}
              />
            </div>

            <div>
              <label className="label">Tipo de turno *</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as '5R' | '3R' })}
                className="input w-full"
                required
              >
                <option value="5R">5 Relevos (5R)</option>
                <option value="3R">3 Relevos (3R)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                5R: Nave 1-4, Impresora, Calidad, Laboratorio, Nanopack, Otros | 3R: Plisado
              </p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
                Turno activo
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
            <Link
              to="/configuration/shifts"
              className="btn btn-outline"
            >
              Cancelar
            </Link>
            <button
              type="submit"
              className="btn btn-primary flex items-center gap-2"
              disabled={loading}
            >
              <Save className="h-4 w-4" />
              {loading ? 'Creando...' : 'Crear Turno'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
